import { BxfLink, BxfTargetAtrEnum } from '@blk/bxf-react-components';

import styled from '@emotion/styled';
import { SourceListConstants } from '../constants';
import { ChatBlkTelemetryService } from '../services';
import { ChatBlkPromptSource, telemetryFunctionMap } from '../types';
import { FormattedSourceInfo, getFormattedSourceInfo } from '../utils';

const { SOURCES_SLOT, DATA_CY_SOURCE_LIST } = SourceListConstants;

const SourceSecondaryInfo = styled.p`
    font-weight: 400;
    margin: 0;
`;

const SourceTitle = styled.p`
    margin: 0;
`;

const SourceListContainer = styled.ol`
    list-style-type: decimal;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    padding-left: 1rem;
    margin: 0;
`;

/**
 * Component that renders a list of sources.
 *
 * @param {Object} props - The component props.
 * @param {ChatBlkPromptSource[]} props.sources - An array of source objects to be displayed.
 * @returns {JSX.Element | null} The rendered component or null if no sources are provided.
 *
 * @component
 * @example
 * const sources = [
 *   { id: '1', uri: 'http://example.com', sourceTitle: ['Title 1', 'Subtitle 1'], secondaryInfo: 'Info 1' },
 *   { id: '2', uri: 'http://example.com', sourceTitle: ['Title 2', 'Subtitle 2'], secondaryInfo: 'Info 2' }
 * ];
 * return <SourceList sources={sources} />;
 */
const SourceList = ({
    sources,
}: {
    sources: ChatBlkPromptSource[];
}): JSX.Element => (
    <div slot={SOURCES_SLOT} data-cy={DATA_CY_SOURCE_LIST}>
        <SourceListContainer>
            {sources.map((source: ChatBlkPromptSource) => {
                const { sourceTitle, secondaryInfo } = getFormattedSourceInfo(
                    source
                ) as FormattedSourceInfo;

                return (
                    <li key={source.id}>
                        <div>
                            <SourceTitle>
                                <BxfLink
                                    href={source.uri}
                                    label={sourceTitle[0]}
                                    dataLinkEvent={`chatblk | result | sources | link click | ${sourceTitle[0]}`}
                                    target={BxfTargetAtrEnum.BLANK}
                                    onClick={() =>
                                        ChatBlkTelemetryService.track(
                                            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_SOURCES_ACCORDION_LINK_CLICKED
                                        )
                                    }
                                />
                                {sourceTitle[1]}
                            </SourceTitle>
                            {secondaryInfo !== '' && (
                                <SourceSecondaryInfo>
                                    {secondaryInfo}
                                </SourceSecondaryInfo>
                            )}
                        </div>
                    </li>
                );
            })}
        </SourceListContainer>
    </div>
);

export default SourceList;
